<template>
  <div id="assignment-basic-info">
    <div class="action-area px-8 py-3">
      <!-- 年度が複数ある場合: selectを表示 -->
      <div v-if="hasMultipleYears && hasFiscalYearsData" class="select-wrapper">
        <select class="custom-select" @change="onYearChange" v-model="year">
          <option v-for="year in yearOptions" :key="year.id" :value="year.id">
            {{ year.label }}年度
          </option>
        </select>
      </div>
      <!-- 年度が1つしかない場合: ラベル表記 -->
      <div
        v-else-if="!hasMultipleYears && hasFiscalYearsData"
        class="year-label"
      >
        {{ year }}年度
      </div>
      <!-- 編集ボタン（年度配列が空の場合も非表示-年度が選択できないので編集できないため） -->
      <Button
        color="blue"
        icon="icon-edit-2"
        label="編集する"
        :class="[
          'edit-button',
          { 'visibility-hidden': isEditMode || !hasFiscalYearsData },
        ]"
        @click="onEditClick"
      />
    </div>
    <div class="p-8">
      <div class="flex gap-x-8">
        <div class="w-1/2">
          <TextInput
            label="必要休日日数"
            v-model="requiredLeaveDays"
            placeholder="例)100"
            unit="日"
            :readOnly="!isEditMode"
            isRequired
            :errorMessage="validationErrors.requiredLeaveDays"
          />
        </div>
        <div class="w-1/2">
          <TextInput
            label="有給日数"
            v-model="annualLeaveDays"
            placeholder="例)20"
            unit="日"
            :readOnly="!isEditMode"
            isRequired
            :errorMessage="validationErrors.annualLeaveDays"
          />
        </div>
      </div>
      <div class="flex gap-x-8 mt-6">
        <div class="w-1/2">
          <TextInput
            label="基本乗船期間"
            v-model="basicBoardingPeriod"
            placeholder="例)60"
            unit="日"
            :readOnly="!isEditMode"
            isRequired
            :errorMessage="validationErrors.basicBoardingPeriod"
          />
        </div>
        <div class="w-1/2">
          <TextInput
            label="基本陸上休暇期間"
            v-model="basicLandLeavePeriod"
            placeholder="例)20"
            unit="日"
            :readOnly="!isEditMode"
            isRequired
            :errorMessage="validationErrors.basicLandLeavePeriod"
          />
        </div>
      </div>
    </div>

    <div class="pb-8" v-if="isEditMode">
      <div class="flex gap-x-4 justify-center">
        <Button
          type="filled"
          colorType="cancel"
          label="キャンセル"
          @click="cancelEdit"
        />
        <Button
          type="filled"
          colorType="normal"
          @click="save"
          label="保存する"
        />
      </div>
    </div>
    <!-- {{ crewBasicInfo }} -->
  </div>
</template>

<script>
import Button from "../components/Button.vue";
import TextInput from "../components/form/TextInput.vue";
import { ColorMixin } from "@/mixins";
import AssignmentBasicInfoService from "../service/AssignmentBasicInfoService.js";
import { getCurrentFiscalYear } from "@/utils/DateUtil";

export default {
  name: "AssignmentBasicInfo",
  mixins: [ColorMixin],
  components: {
    Button,
    TextInput,
  },
  props: {
    crewId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isEditMode: false,
      year: "",
      fiscalYearsData: [],
      requiredLeaveDays: "",
      annualLeaveDays: "",
      basicBoardingPeriod: "",
      basicLandLeavePeriod: "",
      validationErrors: {},
    };
  },
  computed: {
    yearOptions() {
      return this.fiscalYearsData
        .map(item => ({
          id: String(item.fiscal_year),
          label: String(item.fiscal_year),
        }))
        .sort((a, b) => Number(a.id) - Number(b.id)); // 古い年度から順に表示
    },
    currentYearData() {
      return this.fiscalYearsData.find(
        item => String(item.fiscal_year) === this.year
      );
    },
    /**
     * 年度が複数あるかどうか
     * @returns {boolean}
     */
    hasMultipleYears() {
      return this.yearOptions && this.yearOptions.length > 1;
    },
    /**
     * 年度データが存在するかどうか
     * @returns {boolean}
     */
    hasFiscalYearsData() {
      return this.fiscalYearsData && this.fiscalYearsData.length > 0;
    },
  },
  watch: {
    isEditMode(newValue) {
      // 編集中の状態を親に通知
      this.$emit("edit-mode-change", newValue);
    },
    currentYearData: {
      handler(newData) {
        if (newData) {
          this.requiredLeaveDays =
            newData.basic_holiday_days != null
              ? String(newData.basic_holiday_days)
              : "";
          this.annualLeaveDays =
            newData.basic_payed_holiday_days != null
              ? String(newData.basic_payed_holiday_days)
              : "";
          this.basicBoardingPeriod =
            newData.basic_boarding_days != null
              ? String(newData.basic_boarding_days)
              : "";
          this.basicLandLeavePeriod =
            newData.basic_ashore_off_days != null
              ? String(newData.basic_ashore_off_days)
              : "";
        } else {
          this.requiredLeaveDays = "-";
          this.annualLeaveDays = "-";
          this.basicBoardingPeriod = "-";
          this.basicLandLeavePeriod = "-";
        }
      },
      immediate: true,
    },
    crewId: {
      handler() {
        this.fetchData();
      },
      immediate: true,
    },
  },
  methods: {
    /** 編集ボタンクリック時の処理 */
    onEditClick() {
      if (!this.currentYearData) {
        // データがない場合は空文字列に設定
        this.requiredLeaveDays = "";
        this.annualLeaveDays = "";
        this.basicBoardingPeriod = "";
        this.basicLandLeavePeriod = "";
      }
      // 編集モードに切り替え
      this.isEditMode = true;
    },

    /** 船員の年度情報一覧取得 */
    async fetchData() {
      if (!this.crewId) return;
      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        this.fiscalYearsData = await AssignmentBasicInfoService.fetch(
          this.crewId
        );
        // 年度選択状況の初期化（当年度を選択、当年度がなければ最新年度）
        if (this.fiscalYearsData.length > 0) {
          const yearIds = this.fiscalYearsData.map(item =>
            String(item.fiscal_year)
          );
          const currentYearValid =
            this.year && yearIds.includes(String(this.year));
          if (!currentYearValid) {
            const sorted = [...this.fiscalYearsData].sort(
              (a, b) => Number(a.fiscal_year) - Number(b.fiscal_year)
            );
            const latestYear = String(sorted[sorted.length - 1].fiscal_year);
            const currentFiscal = String(getCurrentFiscalYear());
            this.year = yearIds.includes(currentFiscal)
              ? currentFiscal
              : latestYear;
          }
        }
      } catch (error) {
        this.fiscalYearsData = [];
        this.$vs.notify({
          title: "エラー",
          text: "年度情報の取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    /** 選択中の年度変更時の処理 */
    onYearChange(event) {
      this.clearErrors();
      this.year = event.target.value;
    },

    /** 編集キャンセル時の処理 */
    cancelEdit() {
      this.clearErrors();
      this.isEditMode = false;
      // 編集前の値に戻す
      if (this.currentYearData) {
        this.requiredLeaveDays =
          this.currentYearData.basic_holiday_days != null
            ? String(this.currentYearData.basic_holiday_days)
            : "";
        this.annualLeaveDays =
          this.currentYearData.basic_payed_holiday_days != null
            ? String(this.currentYearData.basic_payed_holiday_days)
            : "";
        this.basicBoardingPeriod =
          this.currentYearData.basic_boarding_days != null
            ? String(this.currentYearData.basic_boarding_days)
            : "";
        this.basicLandLeavePeriod =
          this.currentYearData.basic_ashore_off_days != null
            ? String(this.currentYearData.basic_ashore_off_days)
            : "";
      }
    },

    /** 編集保存時の処理 */
    save() {
      // バリデーションを実行
      const isValid = this.validate();
      if (!isValid) {
        return;
      }
      this.update();
    },
    /**
     * バリデーションを実行
     * @returns {boolean} バリデーションが成功したかどうか
     */
    validate() {
      this.$set(this, "validationErrors", {});

      // 必要休日日数のバリデーション
      const requiredLeaveDaysStr = String(this.requiredLeaveDays || "");
      if (!requiredLeaveDaysStr || requiredLeaveDaysStr.trim() === "") {
        this.$set(
          this.validationErrors,
          "requiredLeaveDays",
          "日数を入力してください"
        );
      } else {
        const numericRegex = /^[0-9]+$/;
        const numValue = Number(requiredLeaveDaysStr);
        if (!numericRegex.test(requiredLeaveDaysStr) || numValue < 0) {
          this.$set(
            this.validationErrors,
            "requiredLeaveDays",
            "0以上の半角数字で入力してください"
          );
        } else if (numValue > 365) {
          this.$set(
            this.validationErrors,
            "requiredLeaveDays",
            "365以下で入力してください"
          );
        }
      }

      // 有給日数のバリデーション
      const annualLeaveDaysStr = String(this.annualLeaveDays || "");
      if (!annualLeaveDaysStr || annualLeaveDaysStr.trim() === "") {
        this.$set(
          this.validationErrors,
          "annualLeaveDays",
          "日数を入力してください"
        );
      } else {
        const numericRegex = /^[0-9]+$/;
        const numValue = Number(annualLeaveDaysStr);
        if (!numericRegex.test(annualLeaveDaysStr) || numValue < 0) {
          this.$set(
            this.validationErrors,
            "annualLeaveDays",
            "0以上の半角数字で入力してください"
          );
        } else if (numValue > 365) {
          this.$set(
            this.validationErrors,
            "annualLeaveDays",
            "365以下で入力してください"
          );
        }
      }

      // 基本乗船期間のバリデーション
      const basicBoardingPeriodStr = String(this.basicBoardingPeriod || "");
      if (!basicBoardingPeriodStr || basicBoardingPeriodStr.trim() === "") {
        this.$set(
          this.validationErrors,
          "basicBoardingPeriod",
          "日数を入力してください"
        );
      } else {
        const numericRegex = /^[0-9]+$/;
        const numValue = Number(basicBoardingPeriodStr);
        if (!numericRegex.test(basicBoardingPeriodStr) || numValue < 0) {
          this.$set(
            this.validationErrors,
            "basicBoardingPeriod",
            "0以上の半角数字で入力してください"
          );
        } else if (numValue > 365) {
          this.$set(
            this.validationErrors,
            "basicBoardingPeriod",
            "365以下で入力してください"
          );
        }
      }

      // 基本陸上休暇期間のバリデーション
      const basicLandLeavePeriodStr = String(this.basicLandLeavePeriod || "");
      if (!basicLandLeavePeriodStr || basicLandLeavePeriodStr.trim() === "") {
        this.$set(
          this.validationErrors,
          "basicLandLeavePeriod",
          "日数を入力してください"
        );
      } else {
        const numericRegex = /^[0-9]+$/;
        const numValue = Number(basicLandLeavePeriodStr);
        if (!numericRegex.test(basicLandLeavePeriodStr) || numValue < 0) {
          this.$set(
            this.validationErrors,
            "basicLandLeavePeriod",
            "0以上の半角数字で入力してください"
          );
        } else if (numValue > 365) {
          this.$set(
            this.validationErrors,
            "basicLandLeavePeriod",
            "365以下で入力してください"
          );
        }
      }

      // エラーがあるかどうかを返す
      return Object.keys(this.validationErrors).length === 0;
    },
    /**
     * エラーをクリア
     */
    clearErrors() {
      this.$set(this, "validationErrors", {});
    },

    /** 船員の年度情報更新 */
    async update() {
      if (!this.currentYearData || !this.currentYearData.id) {
        this.$vs.notify({
          title: "配乗基本情報登録の失敗",
          text:
            "配乗基本情報登録の登録に失敗しました。時間を空けて再度試みてください。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
        return;
      }

      const payload = {
        basic_ashore_off_days: this.basicLandLeavePeriod
          ? Number(this.basicLandLeavePeriod)
          : null,
        basic_boarding_days: this.basicBoardingPeriod
          ? Number(this.basicBoardingPeriod)
          : null,
        basic_holiday_days: this.requiredLeaveDays
          ? Number(this.requiredLeaveDays)
          : null,
        basic_payed_holiday_days: this.annualLeaveDays
          ? Number(this.annualLeaveDays)
          : null,
      };

      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        await AssignmentBasicInfoService.update(
          this.crewId,
          this.currentYearData.id,
          payload
        );
        this.isEditMode = false;
        this.$vs.notify({
          title: "配乗基本情報登録の成功",
          text: "配乗基本情報の登録に成功しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
        // データを再取得して最新の状態を反映
        await this.fetchData();
      } catch (error) {
        console.error("配乗基本情報の更新に失敗しました:", error);
        this.$vs.notify({
          title: "配乗基本情報登録の失敗",
          text:
            "配乗基本情報登録の登録に失敗しました。時間を空けて再度試みてください。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.action-area {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid $aisea-light-gray;
}

/* selectの外側 (三角を載せるためのコンテナ) */
.select-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  padding: 18px 0px;
}

/* select本体のカスタム */
.custom-select {
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  font-size: 18px;
  font-weight: 600;
  padding-right: 32px;
  font-family: "Hiragino Sans", -apple-system, Roboto, Helvetica, sans-serif;
  color: #626262;
  outline: none;
}

/* カスタム選択アイコン */
.select-wrapper::after {
  content: url("~@/assets/images/crew-management/assignment/arrow-down.svg");
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  pointer-events: none; /* クリックを邪魔しない */
}

/* 年度ラベル表記（年度が1つの場合） */
.year-label {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  font-family: "Hiragino Sans", -apple-system, Roboto, Helvetica, sans-serif;
  color: #626262;
}

/* 編集ボタン：年度表示がない場合でも右に寄せる */
.edit-button {
  margin-left: auto;
}

/* 編集ボタンを非表示にするが、高さは維持 */
.visibility-hidden {
  visibility: hidden;
}
</style>
